

















import Vue from 'vue';
import LoadingPage from '@/components/LoadingPage.vue';
import ProuniEnrollmentLinkGate from '@/components/prouni/ProuniEnrollmentLinkGate.vue';
import Analytics from '@/helpers/analytics';
import prouniAuth from '@/helpers/prouniAuth';
import { getProuniVoucherFromRoute, isProuniVoucher } from '@/helpers/prouniVoucher';
import { getPartnerCouponFromCookie } from '@/helpers/partnerCoupon';
import SentryClient from '@/helpers/sentry';
import viewsNames from './router/viewsNames';
import { Icon, Tooltip } from '@/components/ds';
import {
  showEqualWebDrawer,
  installEqualWebLaunchButtonOffscreenStyle,
} from './helpers/accessibility';
import { initVlibras } from './helpers/vlibras';
import { startTranslationDetection } from './helpers/translationDetection';

const equalWebSiteKey = 'aecf6ffe12fa1c1d26fe0ed3b1d39c4a';
const equalWebIntegrityKey =
  'sha512-3qLj5jbjMQnXk+FqEdVJjUnjJBGuBTRVOwaiT0ms6mQKQcrz4nulBxl2Hsr0/PpvEqdyJsMsU1NB+Mtfzw8hxA==';

type ProuniLinkGateMode = '' | 'login' | 'error';

// Seletor do container do iframe de login dentro do ProuniEnrollmentLinkGate.
const PROUNI_LINK_IFRAME_SELECTOR = '.prouni-link-gate__iframe';

export default Vue.extend({
  components: { LoadingPage, Icon, Tooltip, ProuniEnrollmentLinkGate },
  data() {
    return {
      isLoading: false,
      prouniLinkGateMode: '' as ProuniLinkGateMode,
      prouniLoggedEmail: '',
      prouniVoucher: '',
    };
  },
  computed: {
    isLoadingApp(): boolean {
      return this.$store.getters.isLoadingApp;
    },
  },
  async mounted() {
    // antes de qualquer early return: tradutor ativo é justamente o cenário em que o
    // funil quebra, e queremos o dado inclusive nas rotas que saem cedo daqui
    startTranslationDetection();

    const isLoginValidation = this.$router.currentRoute.name === viewsNames.VALIDATE_LOGIN;
    if (isLoginValidation) return;

    // ProUni is an independent flow with its own pipeline and pocket-only auth.
    // It must not trigger the enrollment bootstrap (which requires an admission).
    const isProuniRoute = (this.$router.currentRoute.path || '').startsWith('/prouni');
    if (isProuniRoute) return;

    await this.bootstrapEnrollment();
  },
  methods: {
    // Corpo do bootstrap, separado do hook para poder ser reexecutado depois do
    // login por iframe do ProUni (o onSuccess do pocket chega bem depois do mount).
    async bootstrapEnrollment(): Promise<void> {
      // Entrada pelo link do ProUni (`/?cupom=PROUNI-XXXXXXXX`). Sem o param, nada
      // aqui roda e a matrícula do processo seletivo comum segue idêntica.
      const prouniVoucher = getProuniVoucherFromRoute(this.$router.currentRoute);
      this.prouniVoucher = prouniVoucher || '';

      if (prouniVoucher) {
        const isAuthenticated = await this.startProuniEnrollmentLink(prouniVoucher);
        // Sem token de admissão não há bootstrap: a tela do gate (login ou recusa)
        // fica no ar em vez de redirecionar o aluno para o processo seletivo.
        if (!isAuthenticated) return;
      }

      try {
        this.isLoading = true;
        const hasEnrollment = await this.$store.dispatch('GET_ENROLLMENT');

        if (prouniVoucher) {
          // Grava o cupom pessoal na matrícula. A aplicação em si já existe e roda
          // sozinha em AcademicFormation.vue — aqui só alimentamos o partnerCode.
          await this.$store.dispatch('UPSERT_ENROLLMENT', { partnerCode: prouniVoucher });

          // Só DEPOIS de o cupom estar persistido: o campo em Dados Pessoais fica
          // travado porque trocá-lo desfaz a bolsa, e não há caminho de reaplicar
          // cupom depois que a matrícula é criada no ERP.
          this.$store.commit('SET_PARTNER_CODE_LOCKED', true);
        } else {
          await this.applyPartnerCoupon(hasEnrollment);
        }

        await this.$store.dispatch('PULL_PIPELINE');
        await this.$store.dispatch('START_PIPELINE');

        const { email, fullName: name } = this.$store.getters.personalData || {};
        Analytics.identify({ id: email, email, name });
      } catch (err) {
        // O aluno de ProUni entrou por link, não pelo processo seletivo: mandá-lo
        // para lá seria um beco sem saída.
        if (prouniVoucher) {
          this.showProuniLinkError();
          return;
        }

        const admissionsUrl: string = process.env.VUE_APP_ADMISSIONS_URL || '/';

        window.location.replace(admissionsUrl);
      } finally {
        this.isLoading = false;
      }

      this.insertEqualWebScript();
    },
    /**
     * Cupom do parceiro (unico): vem no JWT do cookie `d-partner`, não na URL.
     * Grava na matrícula e trava o campo em Dados Pessoais.
     *
     * Roda com `isLoading` ainda `true` de propósito: é o remount do `router-view`
     * que faz o form ler o `partnerCode` novo — prop que muda depois não reflete
     * no campo, porque `PersonalDataForm` copia as props no `data()`.
     */
    async applyPartnerCoupon(hasEnrollment: boolean): Promise<void> {
      const coupon = await getPartnerCouponFromCookie();
      const currentCode = this.$store.getters.partnerCode;

      // Bolsa do ProUni vale mais que campanha de parceiro: quem já é ProUni e
      // volta sem o `?cupom=` (refresh, bookmark) não pode ter o cupom trocado.
      if (!coupon || isProuniVoucher(currentCode)) {
        if (!hasEnrollment) await this.$store.dispatch('UPSERT_ENROLLMENT', {});
        return;
      }

      try {
        // Sessão retomada com o cupom já gravado não repete a mutation.
        if (!hasEnrollment || currentCode !== coupon) {
          await this.$store.dispatch('UPSERT_ENROLLMENT', { partnerCode: coupon });
        }

        // Só depois de persistido, como no ProUni.
        this.$store.commit('SET_PARTNER_CODE_LOCKED', true);
      } catch (err) {
        // Diferente do link do ProUni: este aluno veio pelo processo seletivo e
        // tem matrícula válida. Derrubar o funil por causa de um cupom seria
        // conversão perdida — segue destravado, dá para digitar à mão.
        SentryClient.captureError(err as Error);
      }
    },
    /**
     * Garante a sessão Pocket e troca o cupom pelo `admissions-auth-token`.
     * Retorna `true` quando o bootstrap da matrícula pode seguir.
     */
    async startProuniEnrollmentLink(voucher: string): Promise<boolean> {
      if (!prouniAuth.isLogged()) {
        // Reusa o login por iframe já escrito para o ProUni. O onSuccess retoma
        // o bootstrap inteiro, então esta chamada não continua o fluxo.
        this.prouniLinkGateMode = 'login';
        await this.$nextTick();

        prouniAuth.loginWithIframe(
          {
            onSuccess: () => {
              this.prouniLinkGateMode = '';
              this.bootstrapEnrollment();
            },
            onError: () => {
              this.showProuniLinkError();
            },
          },
          PROUNI_LINK_IFRAME_SELECTOR,
          'prouni-iframe',
        );

        return false;
      }

      try {
        this.isLoading = true;
        await this.$store.dispatch('EXCHANGE_PROUNI_ENROLLMENT_TOKEN', voucher);
        this.prouniLinkGateMode = '';
        return true;
      } catch (err) {
        this.showProuniLinkError();
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    showProuniLinkError(): void {
      this.prouniLoggedEmail = prouniAuth.getPocketProfile()?.email || '';
      this.prouniLinkGateMode = 'error';
      this.isLoading = false;
    },
    insertEqualWebScript(): void {
      if ((window as any).descomplicaEqualWebInjected) {
        return;
      }
      (window as any).descomplicaEqualWebInjected = true;

      (window as any).interdeal = {
        sitekey: equalWebSiteKey,
        hideBtn: true,
        Position: 'Right',
        Menulang: 'PT',
        domains: {
          js: 'https://cdn.equalweb.com/',
          acc: 'https://access.equalweb.com/',
        },
        btnStyle: {
          vPosition: [null, '20%'],
          color: {
            main: '#2E850F',
            second: '#000000',
          },
          scale: [0.5],
          icon: {
            type: 1,
            shape: 'circle',
            outline: false,
          },
        },
      };

      // Accessibility Utilitary
      const scriptElement = document.createElement('script');
      scriptElement.src = 'https://cdn.equalweb.com/core/5.3.1/accessibility.js';
      scriptElement.defer = true;
      scriptElement.integrity = equalWebIntegrityKey;
      scriptElement.crossOrigin = 'anonymous';
      scriptElement.setAttribute('data-cfasync', 'true');
      scriptElement.onload = () => {
        installEqualWebLaunchButtonOffscreenStyle();
        initVlibras();
      };

      document.body.appendChild(scriptElement);

      // Botmaker
      if (process.env.VUE_APP_BOTMAKER_URL) {
        const match = document.cookie.match(RegExp('(?:^|;\\s*)d-profile=([^;]*)'));
        const dProfileCookie = match ? match[1] : '';

        window.BOTMAKER_VAR = {
          cookie_accounts: decodeURIComponent(dProfileCookie),
        };

        const botmaker = document.createElement('script');
        botmaker.type = 'text/javascript';
        botmaker.async = true;
        botmaker.src = process.env.VUE_APP_BOTMAKER_URL;
        document.body.appendChild(botmaker);
      }
    },
    showAccessibilityDrawer() {
      Analytics.track('Open accessibility drawer', {
        origin: 'Botão Acessibilidade',
      });

      showEqualWebDrawer();
    },
  },
});
